import { useMemo } from 'react'
import {
  usePartialSubscription,
  useShippingAddress,
  useSettings
} from '../stateData/hooks'
import { useHasBilling } from './useHasBilling'

/**
 * Interface for all Flip-related feature flags with computed combinations
 * @interface FlipFeatureFlags
 */
export interface FlipFeatureFlags {
  enableShippingAutofillStep: boolean
  enableFlipExpansion: boolean
  enablePersonalizedOnboardingForFlip: boolean
  enableEnrollWithoutBillingForLocales: boolean
  enableExpressCheckoutEasyEnroll: boolean
  enableEwalletFeaturesOnSmallville: boolean
  enableRedesignFlipFlow: boolean
  enableExpressCheckout: boolean
  enableExpressCheckoutAutofill: boolean
  hasAnyFeatureFlagEnabled: boolean
  activeFlags: string[]
  enableCurrencyUpdateBanner: boolean
  enableCurrencyRateText: boolean
  prohibitFreeWordUsage: boolean
  enableEnrollWithoutBillingComplianceException: boolean
}

/**
 * Custom hook that provides centralized access to all Flip feature flags
 * with pre-calculated combinations.
 *
 * @returns {FlipFeatureFlags} Object containing all individual flags, computed combinations, and helpers
 *
 * @example
 * ```typescript
 * const {
 *   enableFlipExpansion,
 *   activeFlags
 * } = useFlipFeatureFlags()
 * ```
 */
export const useFlipFeatureFlags = (): FlipFeatureFlags => {
  const settingsState = useSettings()
  const settings = settingsState?.data
  const partialSubscription = usePartialSubscription()
  const shippingAddress = useShippingAddress()
  const hasBilling = useHasBilling()

  return useMemo(() => {
    const {
      enableShippingAutofillStep = false,
      enableFlipExpansion = false,
      enablePersonalizedOnboardingForFlip = false,
      enableEnrollWithoutBillingForLocales = false,
      enableExpressCheckoutEasyEnroll = false,
      enableEwalletFeaturesOnSmallville = false,
      enableRedesignFlipFlow = false,
      enableExpressCheckout = false,
      enableCurrencyUpdateBanner = false,
      enableCurrencyRateText = false,
      prohibitFreeWordUsage = false,
      enableEnrollWithoutBillingComplianceException = false
    } = settings ?? {}

    const appName = partialSubscription?.data?.appName ?? ''
    const hasShipping = !!shippingAddress?.data?.street1

    const enableExpressCheckoutAutofill =
      (appName.toLowerCase() !== 'smallville' ||
        enableEwalletFeaturesOnSmallville) &&
      !hasShipping &&
      !hasBilling

    const activeFlags: string[] = []
    if (enableShippingAutofillStep)
      activeFlags.push('enableShippingAutofillStep')
    if (enableFlipExpansion) activeFlags.push('enableFlipExpansion')
    if (enablePersonalizedOnboardingForFlip)
      activeFlags.push('enablePersonalizedOnboardingForFlip')
    if (enableEnrollWithoutBillingForLocales)
      activeFlags.push('enableEnrollWithoutBillingForLocales')
    if (enableExpressCheckoutEasyEnroll)
      activeFlags.push('enableExpressCheckoutEasyEnroll')
    if (enableEwalletFeaturesOnSmallville)
      activeFlags.push('enableEwalletFeaturesOnSmallville')
    if (enableRedesignFlipFlow) activeFlags.push('enableRedesignFlipFlow')
    if (enableExpressCheckout) activeFlags.push('enableExpressCheckout')

    const hasAnyFeatureFlagEnabled = activeFlags.length > 0

    return {
      enableShippingAutofillStep,
      enableFlipExpansion,
      enablePersonalizedOnboardingForFlip,
      enableEnrollWithoutBillingForLocales,
      enableExpressCheckoutEasyEnroll,
      enableEwalletFeaturesOnSmallville,
      enableRedesignFlipFlow,
      enableExpressCheckout,
      enableExpressCheckoutAutofill,
      hasAnyFeatureFlagEnabled,
      activeFlags,
      enableCurrencyUpdateBanner,
      enableCurrencyRateText,
      prohibitFreeWordUsage,
      enableEnrollWithoutBillingComplianceException
    }
  }, [settings, partialSubscription, shippingAddress, hasBilling])
}
